import { getColorStyles } from '@risksmart-app/components/src/utils/colours';
import type { GroupByDatePrecision } from '@risksmart-app/shared/reporting/api/schema';
import type { FieldDefinition } from '@risksmart-app/shared/reporting/datasets/types';
import { aggregateTypesNotSupportingLabels } from '@risksmart-app/shared/reporting/dataTypes';
import type { AggregateType } from '@risksmart-app/shared/reporting/schema';
import _ from 'lodash';
import { useColourPalette } from 'src/hooks/useColourPalette';
import { displayTypes } from 'src/pages/custom-datasources/update/display-types';
import { nullDataChartLabel } from 'src/pages/custom-datasources/update/nullData';
import { useCustomDatasourceHelpers } from 'src/pages/custom-datasources/useCustomDatasourceHelpers';

import type {
  ReportField,
  ReportRawDataType,
  Series,
} from './widget-chart/types';

const toNumericY = (value: ReportRawDataType): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const num = parseFloat(value);

    return isNaN(num) ? null : num;
  }

  return null;
};

export type Options = {
  aggregationType: AggregateType | null;
  x1FieldDefinition: FieldDefinition | null;
  x1GroupByDatePrecision: GroupByDatePrecision | null;
  x2FieldDefinition: FieldDefinition | null;
  x2GroupByDatePrecision: GroupByDatePrecision | null;
  reportingData: ReportField[][];
  aggregateFieldDefinition: FieldDefinition | null;
};

/**
 * Converts raw report data into series data as used by charts.
 * It removes entries with null values
 * adds colours and labels to data points
 * sorts data by x-axis
 * converts x values to Date objects for date based fields
 * @param param0
 * @returns
 */
export const useMapReportDataToSeries = ({
  aggregationType,
  x1FieldDefinition,
  x1GroupByDatePrecision,
  x2FieldDefinition,
  x2GroupByDatePrecision,
  reportingData,
  aggregateFieldDefinition,
}: Options): Series[] => {
  const helpers = useCustomDatasourceHelpers();
  const { genericCategoricalPalette } = useColourPalette();

  if (!x1FieldDefinition) {
    const aggregateFieldConfig = aggregateFieldDefinition
      ? displayTypes[aggregateFieldDefinition.displayType]
      : null;

    const fieldData: ReportField | undefined = reportingData?.[0]?.[0];

    const parseFieldValue = (fieldDataValue: ReportRawDataType) => {
      if (!_.isString(fieldDataValue)) {
        return fieldDataValue;
      }

      const num = parseFloat(fieldDataValue);

      if (_.isNaN(num)) {
        return fieldDataValue;
      }

      const decimalIndex = fieldDataValue.indexOf('.');

      if (decimalIndex !== -1 && fieldDataValue.length - decimalIndex - 1 > 2) {
        return num.toFixed(2);
      }

      return fieldDataValue;
    };

    const fieldValue = parseFieldValue(fieldData?.value);

    const valueString = _.isNil(fieldData)
      ? ''
      : _.isNil(fieldValue)
        ? nullDataChartLabel()
        : String(fieldValue);

    const label =
      fieldData &&
      aggregationType &&
      !aggregateTypesNotSupportingLabels.includes(aggregationType)
        ? (aggregateFieldConfig?.getChartLabel({
            fieldData,
            fieldDef: aggregateFieldDefinition!,
            helpers,
            groupByDatePrecision: null,
          }) ?? valueString)
        : fieldData
          ? valueString
          : '';

    return [
      {
        title: '',
        data: [
          {
            x: null,
            y: toNumericY(fieldData?.value ?? null),
            label,
          },
        ],
      },
    ];
  }

  const x1FieldConfig = displayTypes[x1FieldDefinition.displayType];
  const x2FieldConfig = x2FieldDefinition
    ? displayTypes[x2FieldDefinition.displayType]
    : null;
  const seriesData = (
    reportingData?.map((d) =>
      d.length === 2
        ? { x1: d[0].value, y: toNumericY(d[1].value), sourceX1: d[0] }
        : {
            x1: d[0].value,
            sourceX1: d[0],
            x2: d[1].value,
            sourceX2: d[1],
            y: toNumericY(d[2].value),
          }
    ) ?? []
  ).map((d) => {
    const ratingColour = x1FieldConfig.getChartColor?.({
      fieldData: d.sourceX1,
      fieldDef: x1FieldDefinition,
      helpers,
      groupByDatePrecision: x1GroupByDatePrecision,
    });
    const color = ratingColour
      ? getColorStyles(ratingColour).backgroundColor
      : undefined;
    const label = x1FieldConfig.getChartLabel({
      fieldData: d.sourceX1,
      fieldDef: x1FieldDefinition,
      helpers,
      groupByDatePrecision: x1GroupByDatePrecision,
    });

    return {
      ...d,
      x1: d.x1,
      x2: d.x2,
      label,
      color,
    };
  });

  if (x2FieldDefinition) {
    const groupings = _.groupBy(seriesData, 'x2');

    // Chart components derive axis categories from the first series and plot
    // every series positionally, so all series must cover the same x1 values
    // in the same order. Build the union of x1 values (preserving row order,
    // i.e. the report's sort order) and align each series to it below, filling
    // missing x1/x2 combinations with a null y. Without this, a series with
    // more x1 values than series[0] renders bars past the end of the axis
    // categories, which Highcharts labels with raw numeric indices (RSP-5737).
    //
    // x1 values are keyed by a stable string (not the raw value) because
    // ReportRawDataType allows string[] — a Set/=== keyed on the raw value
    // would compare arrays by reference, so identical array x1s from different
    // rows would never dedupe/match, reintroducing duplicate categories and
    // misaligned series.
    const x1Key = (value: ReportRawDataType): string =>
      Array.isArray(value) ? JSON.stringify(value) : String(value);

    const x1Domain: {
      x1: ReportRawDataType;
      label: string;
      color?: string;
    }[] = [];
    const seenX1 = new Set<string>();
    for (const sd of seriesData) {
      const key = x1Key(sd.x1);
      if (!seenX1.has(key)) {
        seenX1.add(key);
        x1Domain.push({ x1: sd.x1, label: sd.label, color: sd.color });
      }
    }

    return Object.keys(groupings).map((x2, i) => {
      const sourceX2 = groupings[x2][0].sourceX2!;
      const label = x2FieldConfig?.getChartLabel?.({
        fieldData: sourceX2,
        fieldDef: x2FieldDefinition,
        helpers,
        groupByDatePrecision: x2GroupByDatePrecision,
      });
      const ratingColour = x2FieldConfig?.getChartColor?.({
        fieldData: sourceX2,
        fieldDef: x2FieldDefinition,
        helpers,
        groupByDatePrecision: x2GroupByDatePrecision,
      });
      const color = ratingColour
        ? getColorStyles(ratingColour).backgroundColor
        : undefined;

      const title = label;

      // Index this series' rows by the stable x1 key so alignment is an O(1)
      // lookup per domain entry (not an O(domain * group) nested scan) and so
      // array-valued x1s match by value rather than reference.
      const byX1 = new Map(groupings[x2].map((g) => [x1Key(g.x1), g] as const));

      return {
        title: String(title),
        color: color ?? genericCategoricalPalette(i),
        hasSubcategory: true,
        data: x1Domain.map((domainEntry) => {
          const sd = byX1.get(x1Key(domainEntry.x1));

          return {
            x: domainEntry.x1,
            y: sd?.y ?? null,
            color: sd?.color ?? domainEntry.color,
            label: domainEntry.label,
          };
        }),
      };
    });
  } else {
    return [
      {
        title: '',
        data: seriesData.map((sd) => ({
          x: sd.x1,
          y: sd.y,
          label: sd.label,
          color: sd.color,
        })),
      },
    ];
  }
};
